import {
  featureBadgeAnchor,
  featureKey,
  type Assembly,
  type Bounds,
  type PanelPlacement,
  type Round,
  type Step,
} from '@vrlearning/lesson-schema';
import { PANO_SPOT } from '../render/pano.js';

/**
 * A world feature's ⓘ on a step (ADR-087): the key its points open by (`wereld:<feature>`), where
 * it sits in the room, and how wide it is drawn.
 */
export type FeatureBadgeEntry = { key: string; at: Point; size: number };

/** A point in the room, metres (lesson-schema's own, read-only). */
type Point = Round['centre'];

/** The smallest a feature's ⓘ is drawn: the largest an object's is (render/badges.ts). */
const MIN_SIZE = 0.34;

/**
 * How wide a feature's ⓘ is at `distance` metres from the eyes: the direction badge's angle (0.4 m
 * at 7 m, 3.3°), since a feature is often 20–40 m off; never smaller than an object's largest.
 */
export function featureBadgeSize(distance: number): number {
  return Math.max(MIN_SIZE, (distance * PANO_SPOT.size) / PANO_SPOT.distance);
}

/**
 * One badge per distinct world feature the step's hotspots name, in hotspot order, where
 * lesson-schema's `featureBadgeAnchor` puts it for the pupil at `rig` and the lectern at `lectern`.
 * A feature the scene does not know -- no `features` at all (a world the headset could not build),
 * none of that name, or one without a round -- gets no badge: its points cannot be opened, and
 * nothing fails.
 */
export function featureBadgesFor(input: {
  step: Pick<Step, 'hotspots'>;
  features: ReadonlyMap<string, Bounds> | undefined;
  rig: { eye: Point; yaw: number; eyeHeight: number };
  lectern: PanelPlacement;
  assembly: Assembly;
}): FeatureBadgeEntry[] {
  const { step, features, rig, lectern, assembly } = input;
  if (!features) return [];
  const entries: FeatureBadgeEntry[] = [];
  const seen = new Set<string>();
  for (const hotspot of step.hotspots ?? []) {
    if (hotspot.feature === undefined) continue;
    const key = featureKey(hotspot.feature);
    if (seen.has(key)) continue;
    seen.add(key);
    const round = features.get(key)?.round;
    if (!round) continue;
    const anchor = featureBadgeAnchor({ round, ...rig, placement: lectern, assembly });
    entries.push({ key, at: anchor.at, size: featureBadgeSize(anchor.distance) });
  }
  return entries;
}
